import { Component, OnInit, Optional, ViewChild } from "@angular/core";
import { TalentService } from "../talent.service";
import { MatDialog } from "@angular/material/dialog";
import { CustomDialogComponent } from "../custom-dialog/custom-dialog.component";
import { marked } from 'marked';
import { HttpClient } from '@angular/common/http';
import { QuillEditorComponent } from 'ngx-quill';
import { convertHtmlToDocx } from './html_to_docx/html_to_docx';

@Component({
    selector: "app-ai-summary",
    templateUrl: "./ai-summary.component.html",
    styleUrls: ["./ai-summary.component.css"],
    standalone: false
})
export class AiSummaryComponent implements OnInit {
  upload_file: any;
  output_summary: any;
  isGenerating: boolean = false;
  @ViewChild(QuillEditorComponent) quillEditor!: QuillEditorComponent;
  ai_version: string = "claude-sonnet-4-6";
  ai_version_selection: any = ["claude-opus-4-8", "claude-opus-4-7", 'claude-opus-4-6', 'claude-sonnet-4-6'];
  run_time: number = 0;
  generatedPdf: any;
  generatedPdfFileName: any;
  resume_action: string = "Resume Summary";
  resume_action_selection: any = ["Resume Summary", "Formatted Resume"];
  resume_report_list: any;
  show_report: boolean = false;
  show_generator: boolean = true;
  show_report_detail: Array<Boolean> = [];
  message: any;
  formatting_file_preview: string = '';
  prompt = "I want a nicely formatted professional resume from following content. Follow the formatting provided in the formatting file markdown in the order it appears. Use the Nooshin Komaee resume as an example. Follow it exactly. Just return the html with the inline styles. Don't use CSS classes. All styles should be inside the HTML components themselves. When returning, return it as just the file with no extra commentary. Don't provide a header (which is the SEQ logo, the address, phone number, and line below) at all. An HTML will be added after your input with that design included. This will eventually be converted to a pdf and a docx. The styles present in the formatting file will need to carry over to the docx and pdf. I want the docx and pdf to look exactly like the HTML, and survive a potential lossy compression to those formats via htmltodocx."
  isHighlighted: { [id: string]: boolean } = {};
  isGeneratingPdf :boolean =false;
  isAdmin: boolean = false
  allResumes: any[] = [];
  currentPage: number = 1;
  totalPages: number = 1;
  perPage: number = 20;
  isLoadingResumes: boolean = false;

  TOTAL_DURATION_MS = 8 * 60 * 1000; // 8 minutes
  MAX_PROGRESS = 95; // never reach 100%
  progressInterval: any = null;
  progressValue: number = 0;

  constructor(private http: HttpClient, private talentService: TalentService, public dialog: MatDialog) {
    this.loadFormattingTemplate();
   }


  startProgress(createdAt: string | null = null) {
  if (this.progressInterval) {
    clearInterval(this.progressInterval);
  }

  // If we have a created_at timestamp, calculate elapsed time from that
  const startTime = createdAt ? new Date(createdAt).getTime() : Date.now();

  this.updateProgress(startTime);
  this.progressInterval = setInterval(() => {
    this.updateProgress(startTime);
  }, 1000);
}

  updateProgress(startTime: number) {
  const elapsed = Date.now() - startTime;
  const raw = (elapsed / this.TOTAL_DURATION_MS) * 100;
  this.progressValue = Math.min(raw, this.MAX_PROGRESS);
}

  stopProgress() {
  if (this.progressInterval) {
    clearInterval(this.progressInterval);
    this.progressInterval = null;
  }
  this.progressValue = 0;
  }

  loadFormattingTemplate() {
  this.http.get('assets/formatting_template.md', { responseType: 'text' }).subscribe(markdown => {
    this.formatting_file_preview = marked(markdown) as string;
  });
}

  loadAllResumes(page: number = 1) {
  this.isLoadingResumes = true;
  this.currentPage = page;
  this.talentService.getAllFormattedResumes(page, this.perPage).subscribe(
    (response) => {
      console.log("All formatted resumes: " + JSON.stringify(response.resumes || response, null, 2))
      this.allResumes = response.resumes;
      this.totalPages = response.total_pages;
      this.isLoadingResumes = false;
    },
    (error) => {
      console.error('Failed to load resumes', error);
      this.isLoadingResumes = false;
    }
    );
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.loadAllResumes(this.currentPage + 1);
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.loadAllResumes(this.currentPage - 1);
    }
  }

  ngOnInit() {
    this.getResumeReport()
    this.talentService.isUserAdmin().subscribe(
      (response) => {
        this.isAdmin = response == 1;
        console.log('isAdmin:', this.isAdmin);
        if (this.isAdmin) {
          this.loadAllResumes();
        }
      }
    );
  }

  ngOnDestroy() {
  this.stopProgress();
}

  getResumeReport() {
  this.talentService.checkResumeStatus().subscribe(response => {
    this.resume_report_list = response.resumes || response;

    const pendingJobs = this.resume_report_list.filter((r: any) => r.status === 0);
    const hasPending = pendingJobs.length > 0;

    if (hasPending && !this.progressInterval) {
      // Use created_at from the most recent pending job
      const createdAt = pendingJobs[0]?.created_at || null;
      this.startProgress(createdAt);
    } else if (!hasPending) {
      this.stopProgress();
    }

    // At 7 minutes, refresh to check if job completed
    const pendingJob = pendingJobs[0];
    if (pendingJob?.created_at) {
      const elapsed = Date.now() - new Date(pendingJob.created_at).getTime();
      const sevenMinutes = 7 * 60 * 1000;
      if (elapsed < sevenMinutes) {
        const timeUntilSevenMin = sevenMinutes - elapsed;
        setTimeout(() => {
          this.getResumeReport();
        }, timeUntilSevenMin);
      } else {
        // Already past 7 minutes, refresh now
        setTimeout(() => this.getResumeReport(), 30000); // retry every 30s after 7min
      }
    }
  });
}

  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return "0 Bytes";
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  formatTime(lastModified) {
    let modified_date = new Date(lastModified);
    var curr_date = modified_date.getDate();
    var curr_month = modified_date.getMonth() + 1; //Months are zero based
    var curr_year = modified_date.getFullYear();
    return curr_year + "-" + curr_month + "-" + curr_date;
  }

  fileUpload(event) {
    this.upload_file = event.target.files[0];
    this.isGenerating = false;
    this.output_summary = null;
  }

  fileDrop(event) {
    this.upload_file = event[0];
  }

  generateAISummary() {
    const SUPPORTED_FORMATS = ['pdf', 'docx'];
    const file = this.upload_file;
    const ext = file.name.split('.').pop()?.toLowerCase();

    if (!SUPPORTED_FORMATS.includes(ext)) {
      window.alert(`Unsupported file format ".${ext}". Please upload a PDF or DOCX file.`);
      return;
    }
    this.isGenerating = true;
    this.output_summary = null;
    let current_time = new Date();
    this.generatedPdf = "";
    let fileUploadFormData = new FormData();
    this.http.get('assets/formatting_template.md', { responseType: 'blob' }).subscribe(blob1 => {
      this.http.get('assets/Nooshin_Komaee_SEQ_Resume.pdf', { responseType: 'blob' }).subscribe(blob2 => {
          const formattingFile = new File([blob1], 'formatting_template.md', { type: 'text/markdown' });
          const exampleResumeFile = new File([blob2], 'Nooshin_Komaee_SEQ_Resume.pdf', { type: 'application/pdf' });

          fileUploadFormData.append("file", this.upload_file);
          fileUploadFormData.append("formatting_file", formattingFile);
          fileUploadFormData.append("example_resume", exampleResumeFile);
          fileUploadFormData.append("ai_version", this.ai_version);
          fileUploadFormData.append("request_type", this.resume_action == 'Resume Summary' ? 'summary' : 'formatted_resume');
          fileUploadFormData.append("prompt", this.prompt);

          this.talentService.generateAIResumeSummary(fileUploadFormData).subscribe(
            (res) => {
              if (this.resume_action == 'Formatted Resume') {
                this.startProgress()
                this.message = "Resume is being generated. Please check the Report tab."
                this.run_time = new Date().getTime() - current_time.getTime();
                this.run_time = Math.round(this.run_time / 100) / 10;
              }
              else {
                this.output_summary = res;

              }
              this.isGenerating = false;
            },
            (error) => {
              window.alert("AI Resume Generation Failed...");
              this.isGenerating = false;
            }
          );
      })
    })
  }

  async generateAISummaryDoc(html_file_id: string, blind = false) {
  this.isGeneratingPdf = true;
  this.generatedPdf = "";

  try {
    const htmlContent = await new Promise<string>((resolve, reject) => {
      this.talentService.getHtmlContent({ filename: html_file_id }).subscribe(
        response => resolve(response),
        error => reject(error)
      );
    });

    const logoResponse = await fetch('assets/images/seq/logo.png');
    const logoBlob = await logoResponse.blob();
    const logoBase64 = await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(logoBlob);
    });

    const blob = await convertHtmlToDocx(htmlContent, logoBase64, blind);
    
    // Trigger download immediately
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    const clean_file_id = html_file_id?.replace(/\.html$/, '');
    const filename = blind 
      ? `${clean_file_id.replace(/SEQ/gi, 'blind')}.docx`
      : `${clean_file_id}.docx`;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    this.generatedPdf = blob;
    this.getResumeReport();
    this.isGeneratingPdf = false;

  } catch (error) {
    window.alert("AI Pdf Resume Generation Failed...");
    this.isGeneratingPdf = false;
  }
}

  toggleHighlight(resumeId: string) {
    this.isHighlighted[resumeId] = !this.isHighlighted[resumeId];
  }

  copyToClipboard(val: string) {
    let selBox = document.createElement("textarea");
    selBox.style.position = "fixed";
    selBox.style.left = "0";
    selBox.style.top = "0";
    selBox.style.opacity = "0";
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand("copy");
    document.body.removeChild(selBox);
  }

  getFile(filename, type) {
    this.talentService.getHtmlContent({ filename: filename }).subscribe(
      response => {
        console.log(response)
        if (type == 'html') {
          this.output_summary = response
          this.quillEditor.quillEditor.clipboard.dangerouslyPasteHTML(this.output_summary);
        }
        else {
          this.generatedPdf = response
          this.generatedPdfFileName = type
        }
      }
    )
  }

  showReport() {
  this.show_report = true;
  this.show_generator = false;
  this.message = '';
  
  this.getResumeReport();  // updates regular user table and starts progress
  
  if (this.isAdmin) {
    this.loadAllResumes();  // refresh admin table too
  }
}
  showGenerator() {
    this.output_summary = ""
    this.show_report = false
    this.show_generator = true
  }
  detail_toggle(id, filename): void {
    if (this.show_report_detail[id] || filename == null) {
      this.show_report_detail[id] = false;
      this.output_summary = ""
      this.generatedPdf = false
    }
    else {
      this.getFile(filename, 'html')
      this.show_report_detail[id] = !this.show_report_detail[id];
    }
  }
  saveHtml(file_id) {
    this.talentService.udpateHtmlContent({ file_id: file_id, summary: this.output_summary })
      .subscribe(response => {
        let title = "Confirmation";
        let message = "Resume changes saved successfully";
        let confirm_data = { title: title, type: "info", message: message };
        const dialogRef = this.dialog.open(CustomDialogComponent, {
          maxWidth: "600px",
          data: confirm_data,
        });
      })

  }
}


